import React, { useEffect, useState } from "react";
import "../../property.css";

// MUI Components
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Container,
    Divider,
    FormControl,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";

// MUI Data Grid
import { DataGrid } from "@mui/x-data-grid";
import { styled } from "@mui/system";

// MUI Icons
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UpdateIcon from "@mui/icons-material/Update";
import DownloadIcon from "@mui/icons-material/Download";
import InfoIcon from "@mui/icons-material/Info";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import HomeWorkIcon from "@mui/icons-material/HomeWork";

// MUI Button with loading state
import LoadingButton from "@mui/lab/LoadingButton";

// Other libraries
import CryptoJS from "crypto-js";
import { useConfirm } from "material-ui-confirm";
import { showSnackbar } from "../../../../components/SnackBar/SnackBar";
import { ApiDetails, KEY } from "../../../../dummyData";
import {Water} from "@mui/icons-material";

// Styled components for DataGrid
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    "& .MuiDataGrid-root": {
        border: "none",
        borderRadius: 12,
        minHeight: "400px"
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "0.95rem",
        fontWeight: 700,
        backgroundColor: "#2C5283",
        color: "#fff",
        borderRadius: "8px 8px 0 0"
    },
    "& .MuiDataGrid-virtualScroller": {
        backgroundColor: "#fff"
    },
    "& .MuiDataGrid-footerContainer": {
        backgroundColor: "#f5f5f5",
        borderRadius: "0 0 8px 8px",
        borderTop: "none"
    },
    "& .MuiDataGrid-row": {
        fontSize: "0.9rem"
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: "#f9fafb"
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#edf2f9"
    },
    "& .MuiDataGrid-cell": {
        borderBottom: "1px solid #f0f0f0",
        padding: "12px 16px"
    },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
        outline: "none"
    },
    "& .vacant-cell": {
        color: "#4caf50",
        fontWeight: "bold"
    },
    "& .occupied-cell": {
        color: "#f57c00",
        fontWeight: "bold"
    }
}));

// Form Section
const FormCard = styled(Card)(({ theme }) => ({
    boxShadow: "0 4px 20px 0 rgba(0, 0, 0, 0.05)",
    borderRadius: "12px",
    marginBottom: "24px",
    overflow: "visible"
}));

// CardHeader styled
const StyledCardHeader = styled(CardHeader)(({ theme }) => ({
    backgroundColor: "#f5f7fa",
    borderBottom: "1px solid #eaecef",
    "& .MuiCardHeader-title": {
        fontSize: "1.1rem",
        fontWeight: 600,
        color: "#2C5283"
    }
}));

// TextField styled
const StyledTextField = styled(TextField)(({ theme }) => ({
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        transition: "all 0.3s ease-in-out",
        "&:hover": {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2C5283"
            }
        },
        "&.Mui-focused": {
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#2C5283",
                borderWidth: "2px"
            }
        }
    },
    "& .MuiInputLabel-root": {
        color: "#555",
        "&.Mui-focused": {
            color: "#2C5283"
        }
    }
}));

export default function UpdateUnitsDetails(props) {
    const { propertyDetails, tenants, setBillTenantsModal, billTenantsModal } = props;
    const [propertyId, setPropertyId] = useState(propertyDetails.property_id);
    const confirm = useConfirm();

    // State management
    let [meterReadings, setMeterReadings] = useState([]);
    let [electricityMeterReadings, setElectricityMeterReadings] = useState([]);
    const [warning, setWarning] = useState("");
    const [property, setProperty] = useState({ expected_income: 0, agency_id: 0 });
    const [unitName, setUnitName] = useState("");
    const [unitMeterReading, setUnitMeterReading] = useState("");
    const [electricityUnitMeterReading, setElectricityUnitMeterReading] = useState("");
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState("");
    const [billLoading, setBillLoading] = useState(false);
    const [addUnitLoading, setAddUnitLoading] = useState(false);
    const [updateUnitsLoading, setUpdateUnitsLoading] = useState(false);
    const [formError, setFormError] = useState("");

    // Decrypt local storage data
    let myMapDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.getItem("myMap"), KEY);
    let portfolioDetailsDecrypted = CryptoJS.AES.decrypt(localStorage.portfolioDetails, KEY);
    let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
    portfolioDetailsDecrypted.toString(CryptoJS.enc.Utf8);

    // Load units on component mount
    useEffect(() => {
        getUnits();
    }, []);

    // API functions
    const getUnits = () => {
        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            body: JSON.stringify({ property_id: propertyId }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setUnits(data);
                    showSnackbar({
                        title: "Success!",
                        message: "Units retrieved successfully.",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to retrieve units. Please try again.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    const addUnit = () => {
        // Form validation
        if (unitName.trim() === "") {
            setFormError("Unit name is required");
            return;
        }

        if (unitMeterReading.trim() === "") {
            setFormError("Water meter reading is required");
            return;
        }

        if (electricityUnitMeterReading.trim() === "") {
            setFormError("Electricity meter reading is required");
            return;
        }

        setFormError("");
        setAddUnitLoading(true);

        fetch(ApiDetails + "pegasus/visionary/property/add/unit", {
            method: "POST",
            body: JSON.stringify({
                property_id: propertyId,
                unit: unitName,
                state: "VACANT",
                meter_reading: unitMeterReading,
                electricity_meter_reading: electricityUnitMeterReading,
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                setAddUnitLoading(false);

                if (response.status === 200) {
                    setUnits(data);
                    getUnits();
                    // Clear form fields after successful submission
                    setUnitName("");
                    setUnitMeterReading("");
                    setElectricityUnitMeterReading("");

                    showSnackbar({
                        title: "Success!",
                        message: "Unit Added Successfully",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                } else if (response.status === 500) {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to add unit. Please try again.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                setAddUnitLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    const DeleteUnit = (unitId) => {
        fetch(ApiDetails + "pegasus/visionary/property/delete/unit", {
            method: "POST",
            body: JSON.stringify({
                property_id: propertyId,
                id: unitId,
            }),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setUnits(data);
                    getUnits();
                    showSnackbar({
                        title: "Success!",
                        message: "Unit deleted successfully.",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to delete unit. Please try again.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    const updateUnits = () => {
        if (meterReadings.length === 0 && electricityMeterReadings.length === 0) {
            showSnackbar({
                title: "Warning!",
                message: "No readings to update. Please enter at least one new reading.",
                type: "warning",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        setUpdateUnitsLoading(true);
        let finalChanges = [];

        if (meterReadings.length >= electricityMeterReadings.length) {
            meterReadings.forEach((reading) => {
                electricityMeterReadings.forEach((readings2) => {
                    if (reading.unit === readings2.unit) {
                        finalChanges = [...finalChanges, {
                            unit: reading.unit,
                            meter_reading: reading.meter_reading,
                            electricity_meter_reading: readings2.electricity_meter_reading,
                        }];
                        meterReadings = meterReadings.filter((r) => r.unit !== readings2.unit);
                        electricityMeterReadings = electricityMeterReadings.filter((r) => r.unit !== readings2.unit);
                    }
                });
            });
        } else {
            electricityMeterReadings.forEach((reading) => {
                meterReadings.forEach((readings2) => {
                    if (reading.unit === readings2.unit) {
                        finalChanges = [...finalChanges, {
                            unit: reading.unit,
                            meter_reading: readings2.meter_reading,
                            electricity_meter_reading: reading.electricity_meter_reading,
                        }];
                        meterReadings = meterReadings.filter((r) => r.unit !== readings2.unit);
                        electricityMeterReadings = electricityMeterReadings.filter((r) => r.unit !== readings2.unit);
                    }
                });
            });
        }

        let finalMeterReadings = meterReadings.map(reading => ({
            ...reading,
            electricity_meter_reading: 0
        }));

        let finalElectricityMeterReadings = electricityMeterReadings.map(reading => ({
            ...reading,
            meter_reading: 0
        }));

        finalChanges = [...finalChanges, ...finalMeterReadings, ...finalElectricityMeterReadings];

        fetch(ApiDetails + "pegasus/visionary/property/update/Units", {
            method: "POST",
            body: JSON.stringify({
                property_id: propertyId,
                units: finalChanges,
            }),
        })
            .then(async (response) => {
                setUpdateUnitsLoading(false);
                if (response.status === 200) {
                    getUnits();
                    setMeterReadings([]);
                    setElectricityMeterReadings([]);

                    showSnackbar({
                        title: "Success!",
                        message: "Units updated successfully.",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to update units. Please try again.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                setUpdateUnitsLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    const downloadUnitReport = () => {
        if (!selectedUnit) {
            showSnackbar({
                title: "Warning!",
                message: "Please select a unit first.",
                type: "warning",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        setBillLoading(true);
        let fileName;

        fetch(ApiDetails + "pegasus/visionary/property/unit/report", {
            method: "POST",
            body: JSON.stringify({
                property_id: propertyId,
                id: selectedUnit,
            }),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    setBillLoading(false);

                    showSnackbar({
                        title: "Success!",
                        message: "Download Will Start Shortly",
                        type: "success",
                        position: { vertical: "bottom", horizontal: "right" },
                    });

                    for (let pair of response.headers.entries()) {
                        if (pair[0] === "content-disposition") {
                            fileName = pair[1];
                        }
                    }

                    let blob = await response.blob();
                    let href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", fileName);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                } else {
                    setBillLoading(false);
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to generate report. Please try again.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                }
            })
            .catch((err) => {
                setBillLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later.",
                    type: "error",
                    position: { vertical: "bottom", horizontal: "right" },
                });
            });
    };

    // DataGrid column definitions
    const unitsColumns = [
        {
            field: "unit",
            headerName: "Unit",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeWorkIcon color="primary" fontSize="small" />
                    <Typography variant="body2" fontWeight="medium">
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "state",
            headerName: "Status",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: '16px',
                    backgroundColor: params.value === 'VACANT' ? '#e8f5e9' : '#fff3e0',
                    color: params.value === 'VACANT' ? '#2e7d32' : '#e65100',
                    display: 'inline-flex',
                    alignItems: 'center'
                }}>
                    <Typography variant="body2" fontWeight="bold">
                        {params.value}
                    </Typography>
                </Box>
            )
        },
        {
            field: "meter_reading",
            headerName: "Water Meter",
            minWidth: 120,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Water color="info" fontSize="small" />
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: "electricity_meter_reading",
            headerName: "Electricity Meter",
            minWidth: 150,
            flex: 1,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ElectricBoltIcon color="warning" fontSize="small" />
                    <Typography variant="body2">{params.value}</Typography>
                </Box>
            )
        },
        {
            field: "water",
            headerName: "Update Water",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => (
                <StyledTextField
                    type="number"
                    size="small"
                    variant="outlined"
                    placeholder="Enter reading"
                    InputProps={{
                        startAdornment: <Water color="info" fontSize="small" sx={{ mr: 1 }} />
                    }}
                    onChange={(e) => {
                        const newReading = parseFloat(e.target.value);
                        if (isNaN(newReading)) return;

                        const filteredReadings = meterReadings.filter(
                            (reading) => reading.unit !== params.row.unit
                        );

                        setMeterReadings([
                            ...filteredReadings,
                            {
                                unit: params.row.unit,
                                meter_reading: newReading,
                            },
                        ]);
                    }}
                />
            ),
        },
        {
            field: "electricity",
            headerName: "Update Electricity",
            minWidth: 180,
            flex: 1,
            renderCell: (params) => (
                <StyledTextField
                    type="number"
                    size="small"
                    variant="outlined"
                    placeholder="Enter reading"
                    InputProps={{
                        startAdornment: <ElectricBoltIcon color="warning" fontSize="small" sx={{ mr: 1 }} />
                    }}
                    onChange={(e) => {
                        const newReading = parseFloat(e.target.value);
                        if (isNaN(newReading)) return;

                        const filteredReadings = electricityMeterReadings.filter(
                            (reading) => reading.unit !== params.row.unit
                        );

                        setElectricityMeterReadings([
                            ...filteredReadings,
                            {
                                unit: params.row.unit,
                                electricity_meter_reading: newReading,
                            },
                        ]);
                    }}
                />
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 120,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete unit">
                    <IconButton
                        color="error"
                        size="small"
                        onClick={async () => {
                            let deleteUnit = false;

                            await confirm({
                                title: "Delete Unit",
                                description: `Are you sure you want to delete unit ${params.row.unit}?`,
                                confirmationText: "Delete",
                                cancellationText: "Cancel",
                                confirmationButtonProps: { color: "error" }
                            })
                                .then(() => {
                                    deleteUnit = true;
                                })
                                .catch(() => {
                                    deleteUnit = false;
                                });

                            if (deleteUnit) {
                                DeleteUnit(params.row.id);
                            }
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            ),
        },
    ];

    if (propertyDetails.property_id === "") return (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
            <Typography variant="h6">Loading property details...</Typography>
        </Box>
    );

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    mb: 4,
                    borderRadius: 2,
                    background: "linear-gradient(135deg, #2C5283 0%, #1a365d 100%)",
                    color: "white"
                }}
            >
                <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
                    Property Units Management
                </Typography>
                <Typography variant="subtitle1" align="center">
                    {propertyDetails.property_name || "Property"} • Manage Units and Readings
                </Typography>
            </Paper>

            {/* Report Generation Section */}
            <FormCard>
                <StyledCardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <DownloadIcon />
                            <span>Generate Unit Report</span>
                        </Box>
                    }
                />
                <CardContent>
                    <Grid container spacing={3} alignItems="center" justifyContent="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel id="unit-select-label">Select Unit</InputLabel>
                                <Select
                                    labelId="unit-select-label"
                                    id="unit-select"
                                    value={selectedUnit}
                                    onChange={(e) => setSelectedUnit(e.target.value)}
                                    label="Select Unit"
                                    disabled={!units.length}
                                >
                                    {units.length ? (
                                        units.map((unit, index) => (
                                            <MenuItem key={index} value={unit.id}>
                                                {unit.unit}
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem value="">
                                            <em>No units available</em>
                                        </MenuItem>
                                    )}
                                </Select>
                                <FormHelperText>Select a unit to generate report</FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<DownloadIcon />}
                                loading={billLoading}
                                loadingPosition="start"
                                onClick={downloadUnitReport}
                                disabled={!selectedUnit || !!warning}
                                sx={{ px: 4, borderRadius: 2 }}
                            >
                                Generate Report
                            </LoadingButton>
                        </Grid>
                    </Grid>

                    {warning && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {warning}
                        </Alert>
                    )}
                </CardContent>
            </FormCard>

            {/* Add New Unit Section */}
            <FormCard>
                <StyledCardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <AddCircleIcon />
                            <span>Add New Unit</span>
                        </Box>
                    }
                />
                <CardContent>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Unit Number/Name"
                                variant="outlined"
                                fullWidth
                                value={unitName}
                                onChange={(e) => setUnitName(e.target.value)}
                                placeholder="e.g. A1, 101, Ground Floor"
                                InputProps={{
                                    startAdornment: <HomeWorkIcon color="primary" sx={{ mr: 1 }} />,
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Initial Water Meter Reading"
                                variant="outlined"
                                type="number"
                                fullWidth
                                value={unitMeterReading}
                                onChange={(e) => setUnitMeterReading(e.target.value)}
                                placeholder="e.g. 0"
                                InputProps={{
                                    startAdornment: <Water color="info" sx={{ mr: 1 }} />,
                                }}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <StyledTextField
                                label="Initial Electricity Meter Reading"
                                variant="outlined"
                                type="number"
                                fullWidth
                                value={electricityUnitMeterReading}
                                onChange={(e) => setElectricityUnitMeterReading(e.target.value)}
                                placeholder="e.g. 0"
                                InputProps={{
                                    startAdornment: <ElectricBoltIcon color="warning" sx={{ mr: 1 }} />,
                                }}
                                required
                            />
                        </Grid>
                    </Grid>

                    {formError && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {formError}
                        </Alert>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                        <LoadingButton
                            variant="contained"
                            color="success"
                            startIcon={<AddCircleIcon />}
                            loading={addUnitLoading}
                            loadingPosition="start"
                            onClick={addUnit}
                            sx={{ borderRadius: 2 }}
                        >
                            Add Unit
                        </LoadingButton>
                    </Box>
                </CardContent>
            </FormCard>

            {/* Units DataGrid Section */}
            <FormCard>
                <StyledCardHeader
                    title={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "space-between" }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <HomeWorkIcon />
                                <span>Manage Property Units</span>
                            </Box>
                            <Tooltip title="Enter new readings in the update fields to track water and electricity consumption">
                                <IconButton size="small">
                                    <InfoIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    }
                />
                <CardContent>
                    {units.length > 0 ? (
                        <Box sx={{ height: 500, width: "100%" }}>
                            <StyledDataGrid
                                rows={units}
                                columns={unitsColumns}
                                pageSize={10}
                                rowsPerPageOptions={[5, 10, 20]}
                                disableSelectionOnClick
                                getRowClassName={(params) =>
                                    params.row.state === "VACANT" ? "vacant-row" : "occupied-row"
                                }
                                initialState={{
                                    sorting: {
                                        sortModel: [{ field: "unit", sort: "asc" }],
                                    },
                                }}
                            />

                            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    startIcon={<UpdateIcon />}
                                    loading={updateUnitsLoading}
                                    loadingPosition="start"
                                    onClick={updateUnits}
                                    sx={{ borderRadius: 2 }}
                                    disabled={meterReadings.length === 0 && electricityMeterReadings.length === 0}
                                >
                                    Update Readings
                                </LoadingButton>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ py: 4, textAlign: "center" }}>
                            <Typography variant="body1" color="textSecondary" gutterBottom>
                                No units have been added to this property yet.
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Use the "Add New Unit" section above to add your first unit.
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </FormCard>
        </Container>
    );
}